import { 
  sendDriverApprovalEmail, 
  sendCertificateRequestEmail, 
  isEmailConfigured
} from '../services/emailService';
import { useAuthContext } from '../sections/auth';

/**
 * Handles the complete workflow for creating a DriverApprovalRequest and sending email
 * @param {Object} driverRequest - The created driver approval request
 * @param {Object} template - The email template to use
 * @param {Object} user - The current user
 * @param {Function} createEmailLog - Function to create email log entry
 * @returns {Object} - Result object with success status and details
 */
export const handleDriverApprovalWorkflow = async (
  driverRequest, 
  template, 
  user, 
  createEmailLog,
  policyData = null,
  employeeData = null
) => {
  const result = {
    success: false,
    emailSent: false,
    emailLogCreated: false,
    error: null,
    details: {}
  };

  try {
    // Check if email is configured
    if (!isEmailConfigured('gmail')) {
      result.error = 'Gmail not configured. Please set up Gmail in Settings.';
      result.details.message = 'Request created but email not sent due to Gmail configuration.';
      return result;
    }

    // Send email
    const emailResult = await sendDriverApprovalEmail(driverRequest, template, user, policyData, employeeData);
    result.emailSent = true;
    result.details.emailResult = emailResult;

    // Create email log entry
    if (createEmailLog) {
      const emailLogData = {
        template: { connect: { id: template.id } },
        fromEmail: 'notifications@ascendware.net',
        toEmail: policyData?.agentEmail || driverRequest.insurancePolicy?.agentEmail || 'xopes49335@noidem.com',
        subject: `Driver Approval Request - ${driverRequest.employee?.firstName} ${driverRequest.employee?.lastName}`,
        body: `Driver approval request for ${driverRequest.employee?.firstName} ${driverRequest.employee?.lastName}`,
        sentAt: new Date().toISOString().split('T')[0],
        status: 'sent',
        relatedRecord: driverRequest.id
      };

      await createEmailLog({
        variables: { data: emailLogData }
      });
      result.emailLogCreated = true;
    }

    result.success = true;
    result.details.message = 'Driver approval request created and email sent successfully.';

  } catch (error) {
    console.error('Driver approval workflow error:', error);
    result.error = error.message;
    result.details.message = 'Request created but email sending failed.';
  }

  return result;
};

/**
 * Handles the complete workflow for creating a CertificateRequest and sending email
 * @param {Object} certificateRequest - The created certificate request
 * @param {Object} template - The email template to use
 * @param {Object} user - The current user
 * @param {Function} createEmailLog - Function to create email log entry
 * @returns {Object} - Result object with success status and details
 */
export const handleCertificateRequestWorkflow = async (
  certificateRequest, 
  template, 
  user, 
  createEmailLog,
  policyData = null,
  clientData = null
) => {
  const result = {
    success: false,
    emailSent: false,
    emailLogCreated: false,
    error: null,
    details: {}
  };

  try {
    // Check if email is configured
    if (!isEmailConfigured('gmail')) {
      result.error = 'Gmail not configured. Please set up Gmail in Settings.';
      result.details.message = 'Request created but email not sent due to Gmail configuration.';
      return result;
    }

    // Send email
    const emailResult = await sendCertificateRequestEmail(certificateRequest, template, user, policyData, clientData);
    result.emailSent = true;
    result.details.emailResult = emailResult;

    // Create email log entry
    if (createEmailLog) {
      const emailLogData = {
        template: { connect: { id: template.id } },
        fromEmail: 'notifications@ascendware.net',
        toEmail: policyData?.agentEmail || certificateRequest.insurancePolicy?.agentEmail || 'xopes49335@noidem.com',
        subject: `Certificate Request - ${certificateRequest.client?.name}`,
        body: `Certificate request for ${certificateRequest.client?.name}`,
        sentAt: new Date().toISOString().split('T')[0],
        status: 'sent',
        relatedRecord: certificateRequest.id
      };

      await createEmailLog({
        variables: { data: emailLogData }
      });
      result.emailLogCreated = true;
    }

    result.success = true;
    result.details.message = 'Certificate request created and email sent successfully.';

  } catch (error) {
    console.error('Certificate request workflow error:', error);
    result.error = error.message;
    result.details.message = 'Request created but email sending failed.';
  }

  return result;
};

/**
 * Hook to get email workflow functions with user context
 * @returns {Object} - Object containing workflow functions
 */
export const useEmailWorkflow = () => {
  const { user } = useAuthContext();

  return {
    handleDriverApprovalWorkflow: (driverRequest, template, createEmailLog) => 
      handleDriverApprovalWorkflow(driverRequest, template, user, createEmailLog),
    handleCertificateRequestWorkflow: (certificateRequest, template, createEmailLog) => 
      handleCertificateRequestWorkflow(certificateRequest, template, user, createEmailLog)
  };
};

/**
 * Validates if the email workflow can proceed
 * @param {string} provider - Email provider ('gmail' or 'outlook')
 * @returns {Object} - Validation result
 */
export const validateEmailWorkflow = (provider = 'gmail') => {
  const isConfigured = isEmailConfigured(provider);
  const senderEmail = 'notifications@ascendware.net';

  return {
    canProceed: isConfigured && !!senderEmail,
    isConfigured,
    userEmail: senderEmail,
    message: isConfigured 
      ? 'Email is configured and ready to use.'
      : `${provider} is not configured. Please set up ${provider} in Settings.`
  };
};
